import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { providerListEndpoint } from '../config';
import getHeaders from '../utils/getHeaders';
import patientService from '../services/patientService';
import { logger } from '../logger';

const log = logger();

const updateAddress = async (providerId, patientId, payload) => {
  try {
    const response = await axios.put(
      `${providerListEndpoint}/${providerId}/patients/${patientId}/addresses/${payload.id}`,
      payload,
      getHeaders(),
    );
    return response.data;
  } catch (err) {
    log.error('Error in updateAddress', err.response);
    throw err;
  }
};

const useUpdateAddress = (providerId, patientId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      return updateAddress(providerId, patientId, payload);
    },
    // Keep mutation in memory even after component unmounts
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Optimistically update the UI before the API call completes
    onMutate: async (payload) => {
      const queryKey = ['patient', providerId, patientId];

      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value for rollback
      const previousPatientData = queryClient.getQueryData(queryKey);

      // Optimistically update the patient data
      queryClient.setQueryData(queryKey, (old) => {
        // if (!old) return old

        // Helper to update address in the patient data
        const updateAddressInPatient = (patientData) => {
          const updatedPatient = { ...patientData };
          const addressId = payload.id;
          const addressTypes = payload.attributes.addressTypes || [];

          // Create the updated address object
          const updatedAddress = {
            id: addressId,
            street: payload.attributes.street
              ? [payload.attributes.street]
              : [],
            city: payload.attributes.city || '',
            state: payload.attributes.stateName || '',
            postcode: payload.attributes.postalCode || '',
            countryCode: payload.attributes.countryCode || '',
          };

          // Update each address type
          if (addressTypes.includes('Shipping')) {
            updatedPatient.shippingAddress = updatedAddress;
          }
          if (addressTypes.includes('Billing')) {
            updatedPatient.billingAddress = updatedAddress;
          }
          if (addressTypes.includes('Mailing')) {
            updatedPatient.mailingAddress = updatedAddress;
          }

          return updatedPatient;
        };

        return updateAddressInPatient(old);
      });

      // Return context with the snapshot for rollback
      return { previousPatientData };
    },
    // On success, refetch with no-cache in the background
    onSuccess: async () => {
      const queryKey = ['patient', providerId, patientId];

      // Fetch patient details with no-cache to clear Redis
      log.info(
        'Fetching patient details with cache-control: no-cache to clear Redis',
      );

      const customHeaders = {
        'cache-control': 'no-cache',
      };

      log.info('Custom headers object:', customHeaders);

      // Directly fetch with no-cache header and update cache
      try {
        const response = await patientService.getPatient(
          providerId,
          patientId,
          customHeaders,
        );

        log.info(
          'Patient details fetched successfully with no-cache, updating cache',
        );

        // Update the cache with the fresh data from Redis-bypassing call
        queryClient.setQueryData(queryKey, response.data);
      } catch (err) {
        log.error(
          'Failed to fetch patient details with cache-busting headers',
          err,
        );
        // Even if cache-busting fails, the optimistic update is already applied
        // The user will see the updated data, but Redis cache might be stale
      }

      // Also invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['patientAddresses', providerId, patientId],
      });
    },
    // On error, rollback to the previous value
    onError: (error, _payload, context) => {
      log.error('Failed to update address', error);

      // Rollback to previous data
      if (context?.previousPatientData) {
        queryClient.setQueryData(
          ['patient', providerId, patientId],
          context.previousPatientData,
        );
      }
    },
  });
};

export default useUpdateAddress;
